import { ArrowRight, Clock } from "lucide-react";
import { Link } from "react-router";
import type { AgendaSettings } from "@bookdental/schemas";
import { Field, Toggle, settingsInputCls } from "./primitives";

type Props = {
  values: AgendaSettings;
  onChange: (partial: Partial<AgendaSettings>) => void;
};

/**
 * O que sobrou aqui é o que de fato é da Agenda: como o grid DESENHA.
 *
 * Horário de abertura/fechamento, dias de funcionamento e almoço saíram pra
 * Configurações → Organização → Expediente. Eles não são preferência desta tela: são fato da
 * organização, do mesmo naipe que endereço e telefone — e desde que a Equipe passou a ler o
 * expediente pra dizer se dá pra contar com alguém agora, pertencer a uma tela só virou mentira.
 */
export function ScheduleSection({ values, onChange }: Props) {
  return (
    <div className="space-y-6">
      <Link
        to="/configuracoes/expediente"
        className="flex items-center gap-3 rounded-lg border border-zinc-200 bg-zinc-50/60 px-4 py-3 transition-colors hover:border-brand-300 hover:bg-brand-50/50 dark:border-border dark:bg-muted/30 dark:hover:border-primary/40"
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-cyan-50 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-400">
          <Clock className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-semibold text-zinc-900 dark:text-foreground">
            Horário de funcionamento e dias de atendimento
          </span>
          <span className="block text-[12px] text-zinc-500 dark:text-muted-foreground">
            Ficam em Organização → Expediente — são dados da organização, e a Equipe também os usa.
          </span>
        </span>
        <ArrowRight className="h-4 w-4 shrink-0 text-zinc-400" />
      </Link>

      <Group title="Grade">
        <Toggle
          label="Mostrar fins de semana no grid"
          description="Sábado e domingo aparecem mesmo quando não são dias de funcionamento"
          checked={values.showWeekends}
          onChange={(v) => onChange({ showWeekends: v })}
        />
      </Group>

      <Group title="Intervalos">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Slot visual do grid">
            <select
              className={settingsInputCls}
              value={values.slotDuration}
              onChange={(e) =>
                onChange({
                  slotDuration: e.target.value as AgendaSettings["slotDuration"],
                })
              }
            >
              <option value="15">15 minutos</option>
              <option value="30">30 minutos</option>
              <option value="60">1 hora</option>
            </select>
          </Field>
          <Field label="Duração padrão de nova consulta">
            <select
              className={settingsInputCls}
              value={values.defaultAppointmentDuration}
              onChange={(e) =>
                onChange({ defaultAppointmentDuration: Number(e.target.value) })
              }
            >
              {[15, 30, 45, 60, 90, 120].map((n) => (
                <option key={n} value={n}>
                  {n < 60 ? `${n} min` : n === 60 ? "1 hora" : `${n / 60}h`}
                </option>
              ))}
            </select>
          </Field>
        </div>
      </Group>
    </div>
  );
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-3 text-sm font-semibold text-zinc-900">{title}</h3>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

